import { useCallback, useSyncExternalStore } from "react"

const STORAGE_KEY = "luster-recent-pages-v2"
const MAX_ENTRIES = 10

type RecentEntry = { path: string; ts: number }

const SKIP_PATHS = new Set(["/login", "/logout", "/apply", "/reset-password", "/onboarding"])

const NUKED_PATHS = new Set([
  "/admin",
  "/admin/reports",
  "/creator/codex",
  "/admin/leads",
  "/creator/signal",
  "/creator/glitch-prison",
  "/report/new",
  "/admin/clients",
  "/admin/clients/new",
  "/admin/properties/new",
  "/admin/reports/new",
  "/history",
])

const NUKED_PATH_PREFIXES = ["/v2"]

const LEGACY_REDIRECTS: Record<string, string> = {
  "/not-found": "/404",
}

function read(): RecentEntry[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")
  } catch {
    return []
  }
}

function write(entries: RecentEntry[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
  window.dispatchEvent(new StorageEvent("storage", { key: STORAGE_KEY }))
}

let snapshot = read()

function subscribe(cb: () => void) {
  const handler = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY || e.key === null) {
      snapshot = read()
      cb()
    }
  }
  window.addEventListener("storage", handler)
  return () => window.removeEventListener("storage", handler)
}

function getSnapshot() {
  return snapshot
}

export function recordPageVisit(fullPath: string) {
  const pathnameOnly = fullPath.replace(/\?.*$/, "")
  if (SKIP_PATHS.has(pathnameOnly) || pathnameOnly.startsWith("/test/")) return
  if (NUKED_PATHS.has(pathnameOnly) || NUKED_PATH_PREFIXES.some((prefix) => pathnameOnly.startsWith(prefix))) return

  const canonical = LEGACY_REDIRECTS[pathnameOnly]
  const queryPart = fullPath.includes("?") ? fullPath.slice(fullPath.indexOf("?")) : ""
  const resolved = canonical ? canonical + queryPart : fullPath
  const resolvedBase = resolved.replace(/\?.*$/, "")

  // Remove any entry with the same base path (dedup by route, not by query params)
  const entries = read().filter((e) => e.path.replace(/\?.*$/, "") !== resolvedBase)
  entries.unshift({ path: resolved, ts: Date.now() })
  const trimmed = entries.slice(0, MAX_ENTRIES)
  snapshot = trimmed
  write(trimmed)
}

export function useRecentPages(exclude?: string, limit = 5): RecentEntry[] {
  const entries = useSyncExternalStore(subscribe, getSnapshot)
  const filtered = exclude ? entries.filter((e) => e.path !== exclude) : entries
  return filtered.slice(0, limit)
}
